import { ReactElement, ReactNode } from 'react';
import type { CSSProperties } from 'react';
import { toast } from 'sonner';
import { cn } from '@/utils';
import { X } from 'lucide-react';
import { SuiFrenPortrait } from '@/components/suifren/SuiFrenPortrait';
import { useEventSuiFrenAppearance } from '@/hooks/use-event-suifren';

type NotificationIntent = 'info' | 'success' | 'warning' | 'error';

type ToastAction =
	| {
			label: string;
			onClick: () => void;
	  }
	| undefined;

const DEFAULT_TOAST_ART: Record<NotificationIntent, { accent: string }> = {
	success: { accent: '34 197 94' },
	error: { accent: '244 63 94' },
	warning: { accent: '249 115 22' },
	info: { accent: '59 130 246' },
};

const TOAST_SUINFREN_PRESETS: Record<
	NotificationIntent,
	{
		variant: 'bullshark' | 'capy';
		expression: string;
		zoom: number;
		focusY: number;
		focusX?: number;
		className?: string;
	}
> = {
	success: { variant: 'capy', expression: 'bigSmile', zoom: 1.4, focusY: 0 },
	error: { variant: 'capy', expression: 'shocked', zoom: 1.4, focusY: 4 },
	warning: { variant: 'capy', expression: 'annoyed', zoom: 1.4, focusY: 2 },
	info: { variant: 'bullshark', expression: 'cool', zoom: 1.4, focusY: 1 },
};

export const ToastSuiFrenIllustration = ({ intent }: { intent: NotificationIntent }) => {
	const preset = TOAST_SUINFREN_PRESETS[intent] ?? TOAST_SUINFREN_PRESETS.info;
	const { attributes, accessories } = useEventSuiFrenAppearance({
		variant: preset.variant,
		attributes: { expression: preset.expression } as any,
	});

	return (
		<div className="relative flex h-full w-full items-end justify-center overflow-hidden">
			<SuiFrenPortrait
				attributes={attributes}
				accessories={accessories}
				size="custom"
				frame="none"
				zoom={preset.zoom}
				// focusOffset={{ x: preset.focusX ?? 0, y: preset.focusY }}
				className={cn('h-full w-full rounded-xl', preset.className)}
				showShadow={false}
			/>
		</div>
	);
};

const INTENT_INDICATOR_CLASSES: Record<NotificationIntent, string> = {
	success: 'bg-success/90',
	error: 'bg-destructive/90',
	warning: 'bg-warning/90',
	info: 'bg-primary/90',
};

const clampChannel = (value: number) => Math.max(0, Math.min(255, Math.round(value)));

const hexToRgbTriplet = (value: string): string | null => {
	const hex = value.replace('#', '').trim();
	if (hex.length === 3) {
		const expanded = hex
			.split('')
			.map((char) => char + char)
			.join('');
		return hexToRgbTriplet(expanded);
	}
	if (hex.length !== 6) {
		return null;
	}
	const r = parseInt(hex.slice(0, 2), 16);
	const g = parseInt(hex.slice(2, 4), 16);
	const b = parseInt(hex.slice(4, 6), 16);
	if ([r, g, b].some((channel) => Number.isNaN(channel))) {
		return null;
	}
	return `${r} ${g} ${b}`;
};

const numericListToRgbTriplet = (parts: string[]): string | null => {
	if (parts.length < 3) {
		return null;
	}
	const [r, g, b] = parts.slice(0, 3).map((value) => Number(value));
	if ([r, g, b].some((channel) => !Number.isFinite(channel))) {
		return null;
	}
	return `${clampChannel(r)} ${clampChannel(g)} ${clampChannel(b)}`;
};

const normalizeAccent = (input?: string): string | null => {
	if (!input) {
		return null;
	}
	const trimmed = input.trim();
	if (!trimmed) {
		return null;
	}

	if (trimmed.startsWith('#')) {
		return hexToRgbTriplet(trimmed);
	}

	if (/^rgb/i.test(trimmed)) {
		const matches = trimmed.match(/[\d.]+/g);
		return matches ? numericListToRgbTriplet(matches) : null;
	}

	if (/^\d+(\s+\d+){2}$/.test(trimmed)) {
		return numericListToRgbTriplet(trimmed.split(/\s+/));
	}

	return null;
};

export interface NotificationOptions {
	title?: string;
	description?: string;
	intent?: NotificationIntent;
	duration?: number;
	dismissible?: boolean;
	closeButton?: boolean;
	className?: string;
	icon?: ReactNode;
	action?: ToastAction;
	/**
	 * Optional custom id forwarded to Sonner (allows manual dismiss/update).
	 */
	id?: string;
	/**
	 * When undefined, the id or a hash of the title/description is used.
	 */
	dedupeKey?: string;
	/**
	 * Disable deduplication when set to true.
	 */
	allowDuplicates?: boolean;
	/**
	 * How long (ms) the dedupe lock should live.
	 */
	dedupeWindowMs?: number;
	position?: 'top-right' | 'top-center' | 'top-left' | 'bottom-right' | 'bottom-center' | 'bottom-left';
	/**
	 * Run when the toast closes (dismissed or auto-closed).
	 */
	onClose?: () => void;
	/**
	 * Optional illustration displayed on the right side of the toast.
	 */
	illustration?: string | ReactNode;
	/**
	 * Accent color (hex, rgb string, or space separated) used for the illustration gradient.
	 */
	accentColor?: string;
	/**
	 * Additional inline styles merged into the toast element.
	 */
	style?: CSSProperties;
}

type ToastContentProps = {
	id: string;
	intent: NotificationIntent;
	title?: string;
	description?: string;
	icon?: ReactNode;
	action?: ToastAction;
	dismissible: boolean;
	illustration?: string | ReactNode | null;
	accentCss?: string | null;
	className?: string;
	style?: CSSProperties;
	onDismiss: () => void;
};

const SuigarToastContent = ({
	id,
	intent,
	title,
	description,
	icon,
	action,
	dismissible,
	illustration,
	accentCss,
	className,
	style,
	onDismiss,
}: ToastContentProps) => {
	const indicatorClass = INTENT_INDICATOR_CLASSES[intent];
	const descriptionId = description ? `${id}-description` : undefined;
	const titleId = title ? `${id}-title` : undefined;
	const isImageIllustration = typeof illustration === 'string';

	const handleDismiss = () => {
		onDismiss();
	};

	const handleAction = () => {
		if (action?.onClick) {
			action.onClick();
		}
		onDismiss();
	};

	return (
		<div
			role="status"
			aria-live="polite"
			aria-atomic="false"
			className={cn(
				'relative flex w-[min(360px,calc(100vw-32px))] items-stretch gap-4 overflow-visible rounded-2xl border border-border/70 bg-card/90 p-4 text-left shadow-lg ring-1 ring-border/40 backdrop-blur supports-backdrop-filter:backdrop-blur-lg',
				className,
			)}
			style={style}
		>
			{dismissible ? (
				<button
					type="button"
					onClick={handleDismiss}
					className="absolute -right-2 -top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-background text-muted-foreground shadow-lg transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					aria-label="Dismiss notification"
				>
					<X className="h-4 w-4" aria-hidden />
				</button>
			) : null}
			<div className="flex flex-1 items-start gap-3">
				{icon ? (
					<span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted/50 text-foreground">{icon}</span>
				) : (
					<span className={cn('mt-2 inline-flex h-2.5 w-2.5 rounded-full', indicatorClass)} aria-hidden />
				)}
				<div className="flex min-w-0 flex-1 flex-col gap-1">
					{title ? (
						<p id={titleId} className="text-sm font-semibold leading-snug text-foreground">
							{title}
						</p>
					) : null}
					{description ? (
						<p id={descriptionId} className="text-sm leading-relaxed text-muted-foreground">
							{description}
						</p>
					) : null}
					{action ? (
						<button
							type="button"
							onClick={handleAction}
							className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-primary transition hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							{action.label}
						</button>
					) : null}
				</div>
			</div>
			{illustration ? (
				<div className="relative hidden shrink-0 sm:flex size-18" aria-hidden="true">
					<div
						className="absolute inset-0 rounded-xl opacity-95 overflow-hidden"
						style={
							accentCss
								? {
										backgroundImage: `linear-gradient(135deg, rgba(${accentCss}, 0.22), rgba(${accentCss}, 0.08))`,
									}
								: undefined
						}
					/>
					{isImageIllustration ? (
						<img src={illustration} alt="" className="relative z-1 h-[85%] w-full object-contain" />
					) : (
						<div className="relative z-1 flex h-full w-full items-center justify-center">{illustration}</div>
					)}
				</div>
			) : null}
		</div>
	);
};

const DEFAULT_DURATION = 6_000;
const DEFAULT_DEDUPE_WINDOW_MS = 4_000;
const activeLocks = new Map<string, ReturnType<typeof setTimeout> | null>();

const registerLock = (key: string, windowMs: number) => {
	if (activeLocks.has(key)) {
		return false;
	}

	if (!Number.isFinite(windowMs) || windowMs <= 0) {
		activeLocks.set(key, null);
		return true;
	}

	const timeout = setTimeout(() => {
		activeLocks.delete(key);
	}, windowMs);
	activeLocks.set(key, timeout);
	return true;
};

const releaseLock = (key?: string) => {
	if (!key) return;
	const timeout = activeLocks.get(key);
	if (timeout) {
		clearTimeout(timeout);
	}
	if (activeLocks.has(key)) {
		activeLocks.delete(key);
	}
};

const resolveKey = (options: NotificationOptions): string | null => {
	if (options.allowDuplicates) return null;
	if (options.dedupeKey) return options.dedupeKey;
	if (options.id) return options.id;
	if (!options.title && !options.description) return null;
	return [options.intent ?? 'info', options.title ?? '', options.description ?? ''].join('|');
};

export const showNotification = (options: NotificationOptions) => {
	const {
		title,
		description,
		duration = DEFAULT_DURATION,
		dismissible = true,
		intent = 'info',
		icon,
		action,
		id,
		position,
		dedupeWindowMs = DEFAULT_DEDUPE_WINDOW_MS,
		onClose,
		className,
		closeButton,
		illustration,
		accentColor,
		style: styleOverride,
	} = options;

	const key = resolveKey(options);
	if (key) {
		const registered = registerLock(key, dedupeWindowMs);
		if (!registered) {
			return key;
		}
	}
	const toastId = id ?? key ?? `toast-${Date.now()}`;
	const defaults = DEFAULT_TOAST_ART[intent] ?? DEFAULT_TOAST_ART.info;
	const resolvedIllustration = illustration === null ? null : (illustration ?? <ToastSuiFrenIllustration intent={intent} />);
	const accentRgb = normalizeAccent(accentColor) ?? defaults?.accent ?? null;
	const accentCss = accentRgb ? accentRgb.split(/\s+/).join(', ') : null;
	const mergedStyle: CSSProperties = { ...(styleOverride ?? {}) };
	const isDismissible = closeButton ?? dismissible;

	const renderToast = (internalId: string) => (
		<SuigarToastContent
			id={internalId}
			intent={intent}
			title={title}
			description={description}
			icon={icon}
			action={action}
			dismissible={isDismissible}
			illustration={resolvedIllustration}
			accentCss={accentCss}
			className={className}
			style={mergedStyle}
			onDismiss={() => {
				toast.dismiss(internalId);
			}}
		/>
	);

	const toastHandle = toast.custom((internalId) => renderToast(internalId as string), {
		id: toastId,
		duration,
		position,
		closeButton: false,
		onDismiss: () => {
			releaseLock(key ?? undefined);
			onClose?.();
		},
		onAutoClose: () => {
			releaseLock(key ?? undefined);
			onClose?.();
		},
	});

	return toastHandle as string;
};

export const showSuccessNotification = (options: Omit<NotificationOptions, 'intent'>) => showNotification({ ...options, intent: 'success' });
export const showErrorNotification = (options: Omit<NotificationOptions, 'intent'>) => showNotification({ ...options, intent: 'error' });
export const showWarningNotification = (options: Omit<NotificationOptions, 'intent'>) => showNotification({ ...options, intent: 'warning' });
export const showInfoNotification = (options: Omit<NotificationOptions, 'intent'>) => showNotification({ ...options, intent: 'info' });

export const dismissNotification = (id: string) => {
	releaseLock(id);
	toast.dismiss(id);
};

type CustomRenderer = (id: string) => ReactElement;

interface CustomNotificationOptions {
	id: string;
	duration?: number;
	position?: NotificationOptions['position'];
	onClose?: () => void;
}

export const showCustomNotification = (renderer: CustomRenderer, options: CustomNotificationOptions) => {
	const { id, duration = DEFAULT_DURATION, position, onClose } = options;
	const key = options.id;
	if (!registerLock(key, options.duration ?? DEFAULT_DEDUPE_WINDOW_MS)) {
		return id;
	}

	const toastId = toast.custom((toastId) => renderer(toastId as string), {
		id,
		duration,
		position,
		closeButton: true,
		onDismiss: () => {
			releaseLock(key);
			onClose?.();
		},
		onAutoClose: () => {
			releaseLock(key);
			onClose?.();
		},
	});

	return toastId as string;
};
